import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  Chip,
  Snackbar,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  AutoAwesome,
  Home,
  Event,
  Folder,
  Description,
  Dashboard,
  Logout,
  Policy,
  Menu as MenuIcon,
  Close,
  Assignment
} from '@mui/icons-material';
import ScrumAILogo from '../common/ScrumAILogo';
import InviteNotetakerDialog from '../common/InviteNotetakerDialog';
import SignInDialog from '../common/SignInDialog';
import { useAuth } from '../../context/AuthContext';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, error, clearError, openSignInModal, targetAction } = useAuth();
  
  const [inviteOpen, setInviteOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    setMobileDrawerOpen(false);
    await signOut();
    navigate('/');
  };

  const handleInviteClick = () => {
    if (!user) {
      openSignInModal('inviteNotetaker');
    } else {
      setInviteOpen(true);
    }
  };

  const handleAuthSuccess = () => {
    if (targetAction === 'workspace') {
      navigate('/workspace');
    } else if (targetAction === 'inviteNotetaker') {
      setInviteOpen(true);
    }
  };

  const navItems = [
    { path: '/', label: 'Home', icon: <Home sx={{ fontSize: 20 }} />, end: true },
    { path: '/workspace', label: 'Workspace', icon: <Dashboard sx={{ fontSize: 20 }} />, requiresAuth: true },
    { path: '/meetings', label: 'Meetings', icon: <Event sx={{ fontSize: 20 }} /> },
    { path: '/projects', label: 'Projects', icon: <Folder sx={{ fontSize: 20 }} /> },
    { path: '/templates', label: 'Templates', icon: <Description sx={{ fontSize: 20 }} /> },
    { path: '/jira', label: 'Jira', icon: <Assignment sx={{ fontSize: 20 }} />, requiresAuth: true }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', maxHeight: '100dvh', width: '100vw', maxWidth: '100vw', overflow: 'hidden', bgcolor: 'var(--hp-bg)' }}>
      {/* Top Navigation Bar */}
      <Box
        sx={{
          height: { xs: 58, sm: 64 },
          px: { xs: 1.5, sm: 2.5, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--hp-border)',
          bgcolor: 'var(--hp-surface)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          zIndex: 10,
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 3 } }}>
          {/* Mobile Menu Hamburger Button */}
          <IconButton
            onClick={() => setMobileDrawerOpen(true)}
            size="small"
            aria-label="Open mobile navigation menu"
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'var(--hp-text)',
              p: 0.8,
              borderRadius: '10px',
              bgcolor: 'var(--hp-surface-raised)',
              boxShadow: '2px 2px 5px var(--hp-shadow-dark), -2px -2px 5px var(--hp-shadow-light)'
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Rebranded Logo */}
          <ScrumAILogo
            fontSize="1.25rem"
            iconSize={28}
            showBadge
            clickable
            onClick={() => navigate('/')}
          />

          {/* Desktop Navigation Links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {navItems.map((item) => {
              if (item.requiresAuth && !user) {
                return (
                  <Box
                    key={item.path}
                    onClick={() => openSignInModal('workspace')}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '7px 14px',
                      borderRadius: '12px',
                      color: 'var(--hp-text-secondary)',
                      fontWeight: 500,
                      fontSize: '0.88rem',
                      transition: 'all 0.15s ease',
                      '&:hover': { color: 'var(--hp-primary)', bgcolor: 'var(--hp-primary-light)' }
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </Box>
                );
              }

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  style={({ isActive }) => ({
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 14px',
                    borderRadius: '12px',
                    color: isActive ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
                    backgroundColor: isActive ? 'var(--hp-primary-light)' : 'transparent',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.88rem',
                    transition: 'all 0.15s ease'
                  })}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/* Quick Invite Notetaker Button (Visible on medium+ screens) */}
          <Button
            variant="text"
            size="small"
            onClick={handleInviteClick}
            startIcon={<AutoAwesome sx={{ color: 'var(--hp-primary)', fontSize: 18 }} />}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              textTransform: 'none',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: 'var(--hp-primary)',
              bgcolor: 'var(--hp-primary-light)',
              borderRadius: '10px',
              px: 1.5,
              py: 0.6,
              '&:hover': { bgcolor: '#d9e3ff' }
            }}
          >
            Notetaker
          </Button>

          {/* User Profile or Google Sign In */}
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                src={user.photoURL || undefined}
                alt={user.displayName || 'User'}
                onClick={handleProfileClick}
                sx={{
                  width: { xs: 34, sm: 38 },
                  height: { xs: 34, sm: 38 },
                  cursor: 'pointer',
                  bgcolor: 'var(--hp-primary)',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: '2px 2px 6px var(--hp-shadow-dark), -2px -2px 6px var(--hp-shadow-light)',
                  border: '2px solid var(--hp-surface-raised)',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
              >
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
              </Avatar>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1.5,
                      minWidth: 250,
                      borderRadius: '16px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      p: 1
                    }
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                    {user.displayName || 'Scrum Team Member'}
                  </Typography>
                  <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {user.email || 'No email associated'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={user.isDemo ? 'Demo Mode' : 'Google Account'}
                      color={user.isDemo ? 'default' : 'primary'}
                      sx={{ fontSize: '0.7rem', fontWeight: 600, height: 20 }}
                    />
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={() => { handleMenuClose(); navigate('/'); }}>
                  <ListItemIcon><Home fontSize="small" /></ListItemIcon>
                  ScrumAI Overview
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); navigate('/workspace'); }}>
                  <ListItemIcon><Dashboard fontSize="small" /></ListItemIcon>
                  AI Workspace
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); setInviteOpen(true); }}>
                  <ListItemIcon><AutoAwesome fontSize="small" /></ListItemIcon>
                  Invite Notetaker
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); navigate('/privacy'); }}>
                  <ListItemIcon><Policy fontSize="small" /></ListItemIcon>
                  Privacy Policy
                </MenuItem>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleSignOut} sx={{ color: 'var(--hp-danger)' }}>
                  <ListItemIcon><Logout fontSize="small" sx={{ color: 'var(--hp-danger)' }} /></ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={() => openSignInModal('workspace')}
              startIcon={
                <svg width="15" height="15" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
              }
              sx={{
                bgcolor: '#FFFFFF',
                color: '#1F2937',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: { xs: '0.78rem', sm: '0.85rem' },
                textTransform: 'none',
                px: { xs: 1.2, sm: 2 },
                py: { xs: 0.6, sm: 0.8 },
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                '&:hover': { bgcolor: '#F9FAFB' }
              }}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              bgcolor: 'var(--hp-surface)',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '6px 0 24px rgba(0,0,0,0.1)'
            }
          }
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <ScrumAILogo fontSize="1.15rem" iconSize={26} showBadge />
          <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'var(--hp-text-secondary)' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* User Card in Drawer */}
        {user ? (
          <Box className="neumorphic-card-subtle" sx={{ mb: 2, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={user.photoURL || undefined}
                alt={user.displayName || 'User'}
                sx={{ width: 36, height: 36, bgcolor: 'var(--hp-primary)', fontSize: '0.85rem', fontWeight: 600 }}
              >
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--hp-text)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {user.displayName || 'Scrum Team Member'}
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {user.email || 'Signed In'}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setMobileDrawerOpen(false);
              openSignInModal('workspace');
            }}
            sx={{
              mb: 2,
              bgcolor: 'var(--hp-primary)',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem'
            }}
          >
            Sign In with Google
          </Button>
        )}

        <Divider sx={{ mb: 1.5 }} />

        {/* Navigation Links List */}
        <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const handleClick = () => {
              setMobileDrawerOpen(false);
              if (item.requiresAuth && !user) {
                openSignInModal('workspace');
              } else {
                navigate(item.path);
              }
            };

            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={handleClick}
                  sx={{
                    borderRadius: '12px',
                    py: 1.2,
                    px: 1.5,
                    bgcolor: isActive ? 'var(--hp-primary-light)' : 'transparent',
                    color: isActive ? 'var(--hp-primary)' : 'var(--hp-text)',
                    '&:hover': { bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'var(--hp-primary)' : 'var(--hp-text-secondary)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: isActive ? 700 : 500 }}>
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1.5 }} />

        {/* Drawer Action Footer */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setMobileDrawerOpen(false);
              handleInviteClick();
            }}
            startIcon={<AutoAwesome fontSize="small" />}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: 'var(--hp-border)',
              color: 'var(--hp-primary)',
              fontSize: '0.85rem',
              fontWeight: 600
            }}
          >
            Invite Notetaker
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={() => {
              setMobileDrawerOpen(false);
              navigate('/privacy');
            }}
            startIcon={<Policy fontSize="small" />}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              color: 'var(--hp-text-secondary)',
              fontSize: '0.82rem',
              justifyContent: 'flex-start'
            }}
          >
            Privacy Policy
          </Button>

          {user && (
            <Button
              variant="text"
              fullWidth
              onClick={handleSignOut}
              startIcon={<Logout fontSize="small" />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                color: 'var(--hp-danger)',
                fontSize: '0.82rem',
                justifyContent: 'flex-start',
                '&:hover': { bgcolor: '#ffebee' }
              }}
            >
              Sign Out
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </Box>

      {/* Sign-In Dialog with Calendar Permission Notice */}
      <SignInDialog onSuccess={handleAuthSuccess} />

      {/* Invite Notetaker Modal */}
      <InviteNotetakerDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

      {/* Auth Error Notification */}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={clearError} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clearError} severity="error" sx={{ width: '100%', borderRadius: '12px' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout;
